import { useEffect, useState } from 'react'
import { Alert, Badge, Button, Code, CopyButton, Group, Modal, Stack, Text } from '@mantine/core'
import { useMutation } from '@tanstack/react-query'

import { databaseApi, type QueryStatement, type SlowQuery } from '@/api/database'

import { medido, rotuloDaJanela } from './ordenacao'

/**
 * A query legível e copiável — e honesta sobre o quanto dela a AWS entregou.
 *
 * Existe porque o texto na tabela é cortado em 80 caracteres e o resto vivia num `Tooltip` — que
 * **não deixa selecionar** e **desaparece quando o mouse sai**. Eram as duas metades da
 * reclamação de 18/08: "as consultas estão cortadas e eu não consigo selecionar e colar em algum
 * outro lugar".
 *
 * A segunda metade ficou aberta até 24/08, e era pior que parecia: o `DescribeDimensionKeys`
 * corta o statement em **500 caracteres**, então este painel se chamava "Query completa" e
 * copiava um texto truncado. O botão "Buscar texto completo" vai atrás do texto inteiro, e o
 * aviso dos 500 aparece **antes** do clique — porque a busca pode não vir (`PROCESSING`,
 * `UNAVAILABLE`, chave fora da janela), e aí um painel só com botão voltaria a esconder o corte.
 */
function frasePorMotivo(r: QueryStatement): string {
  if (r.error === 'performance_insights_not_enabled') {
    return 'O Performance Insights não está habilitado nesta instância.'
  }
  if (r.status === 'PROCESSING') {
    return 'O Performance Insights ainda está montando o texto desta query. Tente de novo em alguns minutos.'
  }
  if (r.motivo === 'engine_nao_medido') {
    // Nao nomear engine: a lista muda (MySQL entrou em 24/09) e a frase ficava falsa.
    return 'O texto completo não está disponível para o engine desta instância — a AWS não oferece esta consulta para ele.'
  }
  return 'O Performance Insights não tem mais o texto completo desta query nesta janela.'
}

export function QueryPanel({
  query,
  janelaSegundos,
  horas,
  tenantId,
  dbIdentifier,
  onClose,
}: {
  query: SlowQuery | null
  janelaSegundos?: number
  /** A janela que produziu ESTA listagem — não a do seletor no instante do clique. */
  horas: number
  tenantId: number
  dbIdentifier: string
  onClose: () => void
}) {
  const [completo, setCompleto] = useState<string | null>(null)
  // O texto que veio do details bateu no teto de 4095 da AWS — entao ele NAO e completo.
  const [aindaCortado, setAindaCortado] = useState(false)

  const buscar = useMutation({
    mutationFn: () =>
      databaseApi.getQueryStatement(tenantId, dbIdentifier, query!.query_id!, horas),
    onSuccess: (r) => {
      setCompleto(r.available && r.statement ? r.statement : null)
      setAindaCortado(!!r.statement_truncado)
    },
  })

  // Trocar de linha (ou fechar) não pode herdar o texto da anterior — seria mostrar o SQL de uma
  // query no painel de outra.
  useEffect(() => {
    setCompleto(null)
    setAindaCortado(false)
    buscar.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query?.query_id, query?.query_text])

  const texto = completo ?? query?.query_text ?? ''
  const podeBuscar = !!query?.query_text_truncado && !!query?.query_id && !completo

  return (
    <Modal
      opened={!!query}
      onClose={onClose}
      // NUNCA "Query completa" enquanto o texto é o tokenizado: era o título afirmando o que o
      // sistema não tinha lido.
      // "texto completo" so quando ele E completo: o details tem teto de 4095, e chamar de
      // completo um texto que bateu nele seria a mentira da listagem um nivel acima.
      title={completo && !aindaCortado ? 'Query — texto completo' : 'Query'}
      size="xl"
      centered
    >
      {query && (
        <Stack gap="md">
          <Group gap="xs" wrap="wrap">
            <Badge variant="light">Avg Load {query.avg_load.toFixed(4)}</Badge>
            <Badge variant="light">
              {rotuloDaJanela(janelaSegundos)} {medido(query.calls)}
            </Badge>
            <Badge variant="light">Latência {medido(query.avg_latency_ms, 4)} ms</Badge>
            <Badge variant="light">Tempo total {medido(query.total_time_ms, 2)} ms</Badge>
          </Group>

          {query.query_text_truncado && !completo && (
            <Group justify="space-between" wrap="wrap" gap="xs">
              <Text size="xs" c="dimmed" maw={520}>
                O Performance Insights devolveu só os <strong>primeiros 500 caracteres</strong>
                {' '}desta consulta.
                {!query.query_id && ' E não devolveu a chave que permitiria pedir o resto.'}
              </Text>
              {query.query_id && (
                <Button
                  size="xs"
                  variant="light"
                  loading={buscar.isPending}
                  onClick={() => buscar.mutate()}
                >
                  Buscar texto completo
                </Button>
              )}
            </Group>
          )}

          {/* Recusa da AWS aparece SEM apagar o que já se tinha: perder os 500 caracteres seria
              pior que não ter buscado. */}
          {buscar.isSuccess && !completo && (
            <Alert color="gray" variant="light" py="xs">
              <Text size="xs">{frasePorMotivo(buscar.data)}</Text>
            </Alert>
          )}
          {buscar.isError && (
            <Alert color="red" variant="light" py="xs">
              <Text size="xs">
                A busca do texto completo falhou. O texto abaixo continua sendo o que a listagem
                entregou.
              </Text>
            </Alert>
          )}

          {/* `block` para manter a quebra de linha do SQL; sem Tooltip por cima, senão volta a
              ser impossível selecionar. */}
          <Code block style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 420, overflow: 'auto' }}>
            {texto}
          </Code>

          <Group justify="space-between">
            <Text size="xs" c="dimmed" maw={520}>
              {completo && aindaCortado ? (
                <>
                  {texto.length} caracteres, com os <strong>valores literais</strong> como a
                  aplicação os enviou — e este é o <strong>máximo que o Performance Insights
                  entrega</strong>: esta consulta é maior que isso, e o texto continua cortado no
                  fim. Onde aparecer <Code>$1</Code>, <Code>$2</Code> são parâmetros de{' '}
                  <strong>prepared statement</strong>: troque por valores antes de executar. Este
                  acesso fica registrado na auditoria.
                </>
              ) : completo ? (
                <>
                  Texto completo ({texto.length} caracteres), com os <strong>valores literais</strong>
                  {' '}como a aplicação os enviou — este acesso fica registrado na auditoria. Onde
                  aparecer <Code>$1</Code>, <Code>$2</Code> são parâmetros de{' '}
                  <strong>prepared statement</strong>: troque por valores antes de executar.
                </>
              ) : (
                <>
                  Os valores literais aparecem como <Code>?</Code> — o Performance Insights agrupa
                  a consulta por forma, não por texto exato. Por isso ela{' '}
                  <strong>não roda como está</strong> no Query Runner: troque cada{' '}
                  <Code>?</Code> por um valor antes de executar.
                </>
              )}
            </Text>
            {/* Copia o que está NA TELA: depois da busca, o inteiro. Copiar o de 500 com o
                inteiro visível seria a mentira de 18/08 de volta. */}
            <CopyButton value={texto}>
              {({ copied, copy }) => (
                <Button size="xs" variant={copied ? 'light' : 'filled'} onClick={copy}>
                  {copied ? 'Copiado' : 'Copiar query'}
                </Button>
              )}
            </CopyButton>
          </Group>
        </Stack>
      )}
    </Modal>
  )
}
